import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getContrastTextColor,
  getStoreBanner,
  getStoreLogo,
  normalizeHexColor,
  resolveAssetUrl,
} from "../../../utils/helpers/storeSetting";
import { getQrisMerchantName, validateQrisPayload } from "../../../utils/helpers/qris";
import { decodeQrisImageFile } from "../../../utils/helpers/qrisImage";
import { prepareImageFile, validateImageFile } from "../../../utils/helpers/imageUpload";
import { FieldFeedback, FormValidationNotice, RequiredMark } from "../../../components/FormValidation";
import useFormValidation from "../../../hooks/useFormValidation";
import { validationRules as rule } from "../../../utils/helpers/formValidation";

const COLOR_PRESETS = [
  "#0B2D32",
  // "#2563EB", "#7C3AED", "#DB2777", "#E85D3F", "#0891B2", "#16A34A"
];

const useFilePreview = (file, fallback) => {
  const [preview, setPreview] = useState(fallback);

  useEffect(() => {
    if (!(file instanceof File)) {
      setPreview(fallback);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file, fallback]);

  return preview;
};

const ImageInput = ({ label, preview, placeholder, onChange, helper, error }) => (
  <div className="mb-3">
    <label className="form-label">{label}</label>
    <div className="row g-3 align-items-center">
      <div className="col-auto">
        <span
          className="avatar avatar-xl rounded bg-light border text-secondary"
          style={{
            backgroundImage: preview ? `url(${preview})` : "none",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            width: "5.5rem",
            height: "5.5rem",
          }}>
          {!preview ? <span className="small text-center px-2 fs-6">{placeholder}</span> : null}
        </span>
      </div>
      <div className="col">
        <input
          type="file"
          className={`form-control ${error ? "is-invalid" : ""}`}
          accept="image/png,image/jpeg,image/webp"
          onChange={onChange}
        />
        <div className="form-hint">{helper}</div>
        {error ? <div className="invalid-feedback d-block">{error}</div> : null}
      </div>
    </div>
  </div>
);

const BrandColorInput = ({ value, onChange, error, onBlur }) => {
  const color = normalizeHexColor(value);

  return (
    <div className="store-brand-color-card">
      <div className="store-brand-color-copy">
        <span className="store-form-kicker">Brand & tampilan</span>
        <h4>Warna utama aplikasi</h4>
        <p>Warna ini digunakan untuk tombol, menu aktif, grafik, badge, dan elemen penting lainnya.</p>
        <div className="store-color-controls">
          <label className="store-color-picker" title="Pilih warna">
            <input
              type="color"
              value={color}
              onChange={(event) => onChange(event.target.value.toUpperCase())}
              disabled
            />
            <span style={{ background: color }} />
          </label>
          <input
            type="text"
            name="primary_color"
            className={`form-control store-color-hex${error ? " is-invalid" : ""}`}
            value={value || color}
            maxLength={7}
            aria-label="Kode warna utama"
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            onBlur={(event) => {
              onChange(normalizeHexColor(event.target.value));
              onBlur?.();
            }}
          />
          <div className="store-color-presets" aria-label="Pilihan warna cepat">
            {COLOR_PRESETS.map((preset) => (
              <button
                type="button"
                key={preset}
                className={color === preset ? "active" : ""}
                style={{ background: preset }}
                aria-label={`Pilih warna ${preset}`}
                onClick={() => onChange(preset)}
              />
            ))}
          </div>
        </div>
      </div>
      <FieldFeedback
        formId="store"
        name="primary_color"
        error={error}
        hint="Gunakan format HEX 6 digit, misalnya #0B2D32."
      />
      <div
        className="store-theme-preview"
        style={{ "--preview-primary": color, "--preview-contrast": getContrastTextColor(color) }}>
        <div className="store-theme-preview-sidebar">
          <i />
          <i />
          <i />
        </div>
        <div className="store-theme-preview-main">
          <span>Preview tampilan</span>
          <strong>Dashboard Toko</strong>
          <div>
            <i />
            <i />
            <i />
          </div>
          <button type="button">Tombol utama</button>
        </div>
      </div>
    </div>
  );
};

const FormStore = (props) => {
  const { isEdit, closeModal, formState, setFormState, isLoading, handleAdd, handleUpdate } = props;
  const [isScanningQris, setIsScanningQris] = useState(false);
  const [qrisScanError, setQrisScanError] = useState("");
  const [qrisPreviewFailed, setQrisPreviewFailed] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const formRef = useRef(null);
  const validationSchema = useMemo(
    () => ({
      name: [rule.required("Nama toko wajib diisi."), rule.maxLength(120)],
      legal_name: rule.maxLength(150),
      email: [rule.email("Format email toko belum valid."), rule.maxLength(120)],
      phone: rule.maxLength(50),
      website: rule.maxLength(150),
      tagline: rule.maxLength(180),
      instagram: rule.maxLength(120),
      facebook: rule.maxLength(120),
      tiktok: rule.maxLength(120),
      address: rule.maxLength(255),
      description: rule.maxLength(500),
      invoice_footer: rule.maxLength(500),
      primary_color: rule.pattern(/^#[0-9A-Fa-f]{6}$/, "Warna utama harus berupa kode HEX 6 digit."),
    }),
    [],
  );
  const validation = useFormValidation({
    values: formState,
    schema: validationSchema,
    formId: "store",
    focusRoot: formRef,
  });
  const qrisPayload = String(formState.qris_payload || "").trim();
  const qrisMerchant = useMemo(() => getQrisMerchantName(qrisPayload, "Merchant QRIS"), [qrisPayload]);
  const qrisPreview = useFilePreview(
    formState.qris_image_file,
    resolveAssetUrl(formState.qris_image_url || formState.qris_image_path || formState.qris_image, ""),
  );
  const logoPreview = useFilePreview(formState.logo_file, getStoreLogo(formState, ""));
  const bannerPreview = useFilePreview(formState.banner_file, getStoreBanner(formState, ""));

  useEffect(() => {
    setQrisPreviewFailed(false);
  }, [qrisPreview]);

  const updateField = (field, value) => {
    setFormState((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const resetQrisScanner = () => {
    setIsScanningQris(false);
    setQrisScanError("");
    setQrisPreviewFailed(false);
  };

  const handleClose = () => {
    resetQrisScanner();
    setImageErrors({});
    closeModal();
  };

  const handleBrandImageChange = (field, label, preset) => async (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      updateField(field, null);
      setImageErrors((previous) => ({ ...previous, [field]: "" }));
      return;
    }

    try {
      const compressedFile = await prepareImageFile(file, { label, preset });
      updateField(field, compressedFile);
      setImageErrors((previous) => ({ ...previous, [field]: "" }));
    } catch (error) {
      setImageErrors((previous) => ({ ...previous, [field]: error?.message || `${label} tidak valid.` }));
      event.target.value = "";
    }
  };

  const handleQrisImageChange = async (event) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      updateField("qris_image_file", null);
      resetQrisScanner();
      return;
    }

    try {
      validateImageFile(file, { label: "Gambar QRIS", preset: "qris" });
      const compressedFile = await prepareImageFile(file, { label: "Gambar QRIS", preset: "qris" });
      updateField("qris_image_file", compressedFile);
    } catch (error) {
      setQrisScanError(error?.message || "Gambar QRIS tidak valid.");
      event.target.value = "";
      return;
    }

    setIsScanningQris(true);
    setQrisScanError("");

    try {
      const payload = await decodeQrisImageFile(file);
      setFormState((previous) => ({
        ...previous,
        qris_payload: payload,
      }));
    } catch (error) {
      updateField("qris_payload", "");
      setQrisScanError(error?.message || "Payload QRIS tidak dapat dibaca.");
    } finally {
      setIsScanningQris(false);
    }
  };

  const handleManualQrisPayload = (event) => {
    updateField("qris_payload", event.target.value.trim());
    setQrisScanError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validation.validateAll()) return;

    if (formState.qris_image_file && !qrisPayload) {
      setQrisScanError("Gambar QRIS belum berhasil dibaca. Gunakan gambar yang lebih jelas atau isi payload manual.");
      return;
    }

    if (qrisPayload) {
      try {
        validateQrisPayload(qrisPayload);
      } catch (error) {
        setQrisScanError(error?.message || "Payload QRIS tidak valid.");
        return;
      }
    }

    const success = isEdit ? await handleUpdate() : await handleAdd();

    if (success !== false) {
      resetQrisScanner();
      document.querySelector("#modal-store-form .btn-close")?.click();
    }
  };

  return (
    <div
      className="modal modal-blur fade"
      id="modal-store-form"
      tabIndex="-1"
      role="dialog"
      aria-hidden="false"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.3)",
      }}>
      <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
        <form ref={formRef} className="modal-content" onSubmit={handleSubmit} noValidate>
          <div className="modal-header">
            <h5 className="modal-title">Konfigurasi Toko</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={handleClose}></button>
          </div>
          <div className="modal-body">
            <FormValidationNotice errors={validation.errors} />
            <div className="row">
              <div className="col-lg-6">
                <div className="mb-3">
                  <label className="form-label">
                    Nama Toko
                    <RequiredMark />
                  </label>
                  <input
                    {...validation.fieldProps("name")}
                    type="text"
                    className={validation.inputClass("name")}
                    placeholder="contoh: Kopi Nusantara"
                    value={formState.name}
                    required
                    maxLength={120}
                    onChange={(event) => updateField("name", event.target.value)}
                  />
                  <FieldFeedback
                    formId="store"
                    name="name"
                    error={validation.errors.name}
                    hint="Wajib diisi, maksimal 120 karakter."
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mb-3">
                  <label className="form-label">
                    Nama Legal / Perusahaan
                    <RequiredMark optional />
                  </label>
                  <input
                    {...validation.fieldProps("legal_name")}
                    type="text"
                    className={validation.inputClass("legal_name")}
                    placeholder="contoh: CV Kopi Nusantara"
                    value={formState.legal_name}
                    maxLength={150}
                    onChange={(event) => updateField("legal_name", event.target.value)}
                  />
                  <FieldFeedback
                    formId="store"
                    name="legal_name"
                    error={validation.errors.legal_name}
                    hint="Maksimal 150 karakter."
                  />
                </div>
              </div>
              {/* <div className="col-12">
                <div className="card bg-primary-lt border-0 mb-3">
                  <div className="card-body d-flex align-items-start justify-content-between gap-3">
                    <div>
                      <div className="fw-bold text-primary">Multi Outlet / Multi Cabang</div>
                      <div className="text-secondary">
                        Default nonaktif. Aktifkan hanya jika toko ini akan mengelola beberapa outlet/cabang.
                      </div>
                    </div>
                    <label className="form-check form-switch m-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={Boolean(formState.is_multi_outlet)}
                        onChange={(event) => updateField("is_multi_outlet", event.target.checked)}
                      />
                    </label>
                  </div>
                </div>
              </div> */}
              <div className="col-12">
                <BrandColorInput
                  value={formState.primary_color}
                  onChange={(value) => updateField("primary_color", value)}
                  error={validation.errors.primary_color}
                  onBlur={() => validation.validateField("primary_color")}
                />
              </div>
              <div className="col-lg-6">
                <div className="mb-3">
                  <label className="form-label">
                    Email
                    <RequiredMark optional />
                  </label>
                  <input
                    {...validation.fieldProps("email")}
                    type="email"
                    className={validation.inputClass("email")}
                    placeholder="email toko"
                    value={formState.email}
                    maxLength={120}
                    onChange={(event) => updateField("email", event.target.value)}
                  />
                  <FieldFeedback
                    formId="store"
                    name="email"
                    error={validation.errors.email}
                    hint="Jika diisi, gunakan alamat email yang valid."
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mb-3">
                  <label className="form-label">
                    No. Telepon / WhatsApp
                    <RequiredMark optional />
                  </label>
                  <input
                    {...validation.fieldProps("phone")}
                    type="text"
                    className={validation.inputClass("phone")}
                    placeholder="nomor kontak toko"
                    value={formState.phone}
                    maxLength={50}
                    onChange={(event) => updateField("phone", event.target.value)}
                  />
                  <FieldFeedback
                    formId="store"
                    name="phone"
                    error={validation.errors.phone}
                    hint="Maksimal 50 karakter."
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mb-3">
                  <label className="form-label">
                    Website
                    <RequiredMark optional />
                  </label>
                  <input
                    {...validation.fieldProps("website")}
                    type="text"
                    className={validation.inputClass("website")}
                    placeholder="https://domain-toko.com"
                    value={formState.website}
                    maxLength={150}
                    onChange={(event) => updateField("website", event.target.value)}
                  />
                  <FieldFeedback
                    formId="store"
                    name="website"
                    error={validation.errors.website}
                    hint="Maksimal 150 karakter."
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mb-3">
                  <label className="form-label">
                    Tagline
                    <RequiredMark optional />
                  </label>
                  <input
                    {...validation.fieldProps("tagline")}
                    type="text"
                    className={validation.inputClass("tagline")}
                    placeholder="tagline toko"
                    value={formState.tagline}
                    maxLength={180}
                    onChange={(event) => updateField("tagline", event.target.value)}
                  />
                  <FieldFeedback
                    formId="store"
                    name="tagline"
                    error={validation.errors.tagline}
                    hint="Maksimal 180 karakter."
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mb-3">
                  <label className="form-label">
                    Instagram
                    <RequiredMark optional />
                  </label>
                  <input
                    {...validation.fieldProps("instagram")}
                    type="text"
                    className={validation.inputClass("instagram")}
                    placeholder="@namatoko"
                    value={formState.instagram}
                    maxLength={120}
                    onChange={(event) => updateField("instagram", event.target.value)}
                  />
                  <FieldFeedback
                    formId="store"
                    name="instagram"
                    error={validation.errors.instagram}
                    hint="Maksimal 120 karakter."
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="mb-3">
                  <label className="form-label">
                    Facebook
                    <RequiredMark optional />
                  </label>
                  <input
                    {...validation.fieldProps("facebook")}
                    type="text"
                    className={validation.inputClass("facebook")}
                    placeholder="facebook toko"
                    value={formState.facebook}
                    maxLength={120}
                    onChange={(event) => updateField("facebook", event.target.value)}
                  />
                  <FieldFeedback
                    formId="store"
                    name="facebook"
                    error={validation.errors.facebook}
                    hint="Maksimal 120 karakter."
                  />
                </div>
              </div>
              <div className="col-lg-4">
                <div className="mb-3">
                  <label className="form-label">
                    TikTok
                    <RequiredMark optional />
                  </label>
                  <input
                    {...validation.fieldProps("tiktok")}
                    type="text"
                    className={validation.inputClass("tiktok")}
                    placeholder="@namatoko"
                    value={formState.tiktok}
                    maxLength={120}
                    onChange={(event) => updateField("tiktok", event.target.value)}
                  />
                  <FieldFeedback
                    formId="store"
                    name="tiktok"
                    error={validation.errors.tiktok}
                    hint="Maksimal 120 karakter."
                  />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="mb-3">
                  <label className="form-label">
                    Alamat
                    <RequiredMark optional />
                  </label>
                  <textarea
                    {...validation.fieldProps("address")}
                    className={validation.inputClass("address")}
                    rows="2"
                    placeholder="alamat lengkap toko"
                    value={formState.address}
                    maxLength={255}
                    onChange={(event) => updateField("address", event.target.value)}></textarea>
                  <FieldFeedback
                    formId="store"
                    name="address"
                    error={validation.errors.address}
                    hint="Maksimal 255 karakter."
                  />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="mb-3">
                  <label className="form-label">
                    Deskripsi Singkat
                    <RequiredMark optional />
                  </label>
                  <textarea
                    {...validation.fieldProps("description")}
                    className={validation.inputClass("description")}
                    rows="3"
                    placeholder="deskripsi toko untuk halaman publik / invoice"
                    value={formState.description}
                    maxLength={500}
                    onChange={(event) => updateField("description", event.target.value)}></textarea>
                  <FieldFeedback
                    formId="store"
                    name="description"
                    error={validation.errors.description}
                    hint="Maksimal 500 karakter."
                  />
                </div>
              </div>
              <div className="col-lg-12">
                <div className="mb-3">
                  <label className="form-label">Footer Invoice dan Struk</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    placeholder="contoh: Terima kasih telah berbelanja."
                    value={formState.invoice_footer}
                    maxLength={500}
                    onChange={(event) => updateField("invoice_footer", event.target.value)}></textarea>
                  <div className="form-hint">Ditampilkan pada struk POS, hasil cetak, dan PDF invoice.</div>
                </div>
              </div>
              <div className="col-lg-4">
                <ImageInput
                  label="Logo"
                  preview={logoPreview}
                  placeholder="Tambahkan logo"
                  helper="JPG, PNG, atau WEBP (maks. 4 MB). Dioptimalkan untuk sidebar, login, POS, dan invoice."
                  error={imageErrors.logo_file}
                  onChange={handleBrandImageChange("logo_file", "Logo", "brand")}
                />
              </div>
              <div className="col-lg-4">
                <ImageInput
                  label="Banner"
                  preview={bannerPreview}
                  placeholder="Tambahkan banner"
                  helper="JPG, PNG, atau WEBP (maks. 8 MB). Dioptimalkan untuk halaman publik dan branding."
                  error={imageErrors.banner_file}
                  onChange={handleBrandImageChange("banner_file", "Banner", "banner")}
                />
              </div>
              <div className="col-12">
                <div className="store-qris-config-card">
                  <div className="store-qris-config-heading">
                    <div className="store-qris-config-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M4 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5zM14 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V5zM4 15a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-4zM14 14h3M20 14v.01M14 17v3M17 17h3M20 17v3" />
                      </svg>
                    </div>
                    <div>
                      <span className="store-form-kicker">Pembayaran digital</span>
                      <h4>QRIS dinamis toko</h4>
                      <p>
                        Unggah QRIS statis. Akas akan membaca payload dan menyisipkan nominal transaksi secara otomatis
                        di POS.
                      </p>
                    </div>
                    <span
                      className={`store-qris-status ${qrisPayload ? "is-ready" : ""} ${qrisScanError ? "is-error" : ""}`}>
                      {isScanningQris
                        ? "Membaca QR..."
                        : qrisScanError
                          ? "Perlu diperbaiki"
                          : qrisPayload
                            ? "Siap digunakan"
                            : "Belum aktif"}
                    </span>
                  </div>

                  <div className="store-qris-config-body">
                    <div className={`store-qris-preview ${qrisPreview && !qrisPreviewFailed ? "has-image" : ""}`}>
                      {qrisPreview && !qrisPreviewFailed ? (
                        <img
                          src={qrisPreview}
                          alt={`Preview QRIS ${qrisMerchant}`}
                          onError={() => setQrisPreviewFailed(true)}
                        />
                      ) : (
                        <span>{qrisPreviewFailed ? "Preview tidak tersedia" : "QRIS"}</span>
                      )}
                    </div>
                    <div className="store-qris-config-control">
                      <label className="form-label">Gambar QRIS statis</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/png,image/jpeg,image/webp"
                        disabled={isScanningQris}
                        onChange={handleQrisImageChange}
                      />
                      <div className="form-hint">
                        Gunakan JPG, PNG, atau WEBP maksimal 4 MB yang tajam dan tidak terpotong.
                      </div>

                      {isScanningQris ? (
                        <div className="store-qris-feedback is-loading">
                          <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                          Membaca dan memvalidasi payload QRIS...
                        </div>
                      ) : qrisScanError ? (
                        <div className="store-qris-feedback is-error">{qrisScanError}</div>
                      ) : qrisPayload ? (
                        <div className="store-qris-feedback is-success">
                          <strong>Payload valid</strong>
                          <span>{qrisMerchant} · tersimpan per toko</span>
                        </div>
                      ) : (
                        <div className="store-qris-feedback">
                          Upload ulang QRIS lama untuk mengaktifkan nominal otomatis.
                        </div>
                      )}

                      <details className="store-qris-manual">
                        <summary>Input payload manual</summary>
                        <textarea
                          className="form-control mt-2"
                          rows="3"
                          spellCheck="false"
                          placeholder="Tempel string payload QRIS statis di sini"
                          value={formState.qris_payload || ""}
                          onChange={handleManualQrisPayload}></textarea>
                      </details>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-link link-secondary btn-3"
              data-bs-dismiss="modal"
              onClick={handleClose}>
              Batal
            </button>
            <button type="submit" className="btn btn-primary btn-5 ms-auto" disabled={isLoading || isScanningQris}>
              {isScanningQris ? "Membaca QRIS..." : isLoading ? "Menyimpan..." : "Simpan Konfigurasi"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormStore;
